// src/pages/admin/admin.component.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  firestore, 
  collection, 
  addDoc, 
  getDocs, 
  deleteDoc, 
  doc, 
  updateDoc,
  query,
  orderBy
} from '../../firebase/firebase.utils';
import './admin.styles.scss';

const AdminPanel = () => {
  // Products State
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    category: 'new-arrivals',
    image: null,
    imagePreview: null,
    isNew: true
  });
  const [imageError, setImageError] = useState('');
  const [adminName, setAdminName] = useState('');
  const [error, setError] = useState('');

  // Articles State
  const [articles, setArticles] = useState([]);
const [showArticleForm, setShowArticleForm] = useState(false);
const [editingArticle, setEditingArticle] = useState(null);  // ✅ Uncomment this line
const [articleLoading, setArticleLoading] = useState(false);
  const [articleFormData, setArticleFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    category: 'Hair Wigs',
    image: null,
    imagePreview: null,
    date: '',
    readTime: '3 min read'
  });

  // ===== LOAD PRODUCTS =====
  const loadProducts = async () => {
    try {
      setLoading(true);
      setError('');
      
      if (!navigator.onLine) {
        setError('⚠️ You are offline. Please check your internet connection.');
        setLoading(false);
        return;
      }
      
      const productsRef = collection(firestore, 'products');
      const querySnapshot = await getDocs(productsRef);
      
      const productsList = [];
      querySnapshot.forEach((doc) => {
        productsList.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      setProducts(productsList);
      setLoading(false);
    } catch (error) {
      console.error('Error loading products:', error);
      setLoading(false);
      
      if (error.code === 'unavailable' || error.message.includes('offline')) {
        setError('⚠️ Cannot connect to Firebase. Please check your internet connection.');
      } else if (error.message.includes('permission')) {
        setError('⚠️ Permission denied. Please check Firestore rules in Firebase Console.');
      } else {
        setError('❌ Error loading products: ' + error.message);
      }
    }
  };

  // ===== LOAD ARTICLES =====
  const loadArticles = async () => {
    try {
      setArticleLoading(true);
      const articlesRef = collection(firestore, 'articles');
      const q = query(articlesRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const articlesList = [];
      querySnapshot.forEach((doc) => {
        articlesList.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      setArticles(articlesList);
      setArticleLoading(false);
    } catch (error) {
      console.error('Error loading articles:', error);
      setArticleLoading(false);
    }
  };

  // ===== INIT =====
  useEffect(() => {
    loadProducts();
    loadArticles();
    const name = localStorage.getItem('adminUsername') || 'Admin';
    setAdminName(name);
  }, []);

  // ===== PRODUCT FUNCTIONS =====
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setImageError('');
    
    if (file) {
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
      if (!validTypes.includes(file.type)) {
        setImageError('Please upload a valid image (JPEG, PNG, GIF, WEBP, SVG)');
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        setImageError('Image size should be less than 5MB');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          image: file,
          imagePreview: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();
    setError('');
    
    try {
      if (!navigator.onLine) {
        alert('⚠️ You are offline. Please check your internet connection.');
        return;
      }

      const productsRef = collection(firestore, 'products');
      const newProduct = {
        title: formData.title,
        description: formData.description,
        price: formData.price,
        category: formData.category,
        isNew: true,
        image: formData.imagePreview || '/images/default.jpg',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      const docRef = await addDoc(productsRef, newProduct);
      
      setProducts([{ id: docRef.id, ...newProduct }, ...products]);
      resetForm();
      setShowForm(false);
      alert('✅ Product added successfully!');
      
    } catch (error) {
      console.error('Error adding product:', error);
      
      if (error.message.includes('permission')) {
        alert('❌ Permission denied. Please check Firestore rules in Firebase Console.');
      } else if (error.message.includes('offline')) {
        alert('⚠️ You are offline. Please check your internet connection.');
      } else {
        alert('❌ Error adding product: ' + error.message);
      }
    }
  };

  const handleUpdateProduct = async (e) => {
    e.preventDefault();
    setError('');
    
    try {
      if (!navigator.onLine) {
        alert('⚠️ You are offline. Please check your internet connection.');
        return;
      }

      const productRef = doc(firestore, 'products', editingProduct.id);
      const updatedData = {
        title: formData.title,
        description: formData.description,
        price: formData.price,
        category: formData.category,
        image: formData.imagePreview || editingProduct.image || '/images/default.jpg',
        updatedAt: new Date().toISOString()
      };
      
      await updateDoc(productRef, updatedData);
      
      setProducts(products.map(p => 
        p.id === editingProduct.id ? { id: p.id, ...updatedData } : p
      ));
      
      resetForm();
      setShowForm(false);
      setEditingProduct(null);
      alert('✅ Product updated successfully!');
      
    } catch (error) {
      console.error('Error updating product:', error);
      
      if (error.message.includes('permission')) {
        alert('❌ Permission denied. Please check Firestore rules in Firebase Console.');
      } else {
        alert('❌ Error updating product: ' + error.message);
      }
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    try {
      if (!navigator.onLine) {
        alert('⚠️ You are offline. Please check your internet connection.');
        return;
      }

      const productRef = doc(firestore, 'products', productId);
      await deleteDoc(productRef);
      
      setProducts(products.filter(p => p.id !== productId));
      alert('✅ Product deleted successfully!');
      
    } catch (error) {
      console.error('Error deleting product:', error);
      
      if (error.message.includes('permission')) {
        alert('❌ Permission denied. Please check Firestore rules in Firebase Console.');
      } else {
        alert('❌ Error deleting product: ' + error.message);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      price: '',
      category: 'new-arrivals',
      image: null,
      imagePreview: null,
      isNew: true
    });
    setEditingProduct(null);
    setImageError('');
    setError('');
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      title: product.title || '',
      description: product.description || '',
      price: product.price || '',
      category: product.category || 'new-arrivals',
      image: null,
      imagePreview: product.image || null,
      isNew: product.isNew || true
    });
    setShowForm(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-PK', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingProduct) {
      handleUpdateProduct(e);
    } else {
      handleAddProduct(e);
    }
  };

  const getCategoryLabel = (category) => {
    const labels = {
      'men-wigs': 'Men Wigs',
      'lady-hair': 'Lady Hair',
      'new-arrivals': 'New Arrivals',
      'hair-patches': 'Hair Patches',
      'hair-units': 'Hair Units',
      'accessories': 'Accessories'
    };
    return labels[category] || category;
  };

  // ===== ARTICLE FUNCTIONS =====
  const handleArticleInputChange = (e) => {
    const { name, value } = e.target;
    setArticleFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleArticleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setArticleFormData(prev => ({
          ...prev,
          image: file,
          imagePreview: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAddArticle = async (e) => {
    e.preventDefault();
    
    try {
      const articlesRef = collection(firestore, 'articles');
      const newArticle = {
        title: articleFormData.title,
        excerpt: articleFormData.excerpt,
        content: articleFormData.content,
        category: articleFormData.category,
        image: articleFormData.imagePreview || '/images/default-article.jpg',
        date: articleFormData.date || new Date().toISOString().split('T')[0],
        readTime: articleFormData.readTime || '3 min read',
        slug: articleFormData.title.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      const docRef = await addDoc(articlesRef, newArticle);
      
      setArticles([{ id: docRef.id, ...newArticle }, ...articles]);
      resetArticleForm();
      setShowArticleForm(false);
      alert('✅ Article added successfully!');
      
    } catch (error) {
      console.error('Error adding article:', error);
      alert('❌ Error adding article: ' + error.message);
    }
  };

  const handleDeleteArticle = async (articleId) => {
    if (!window.confirm('Are you sure you want to delete this article?')) return;
    
    try {
      const articleRef = doc(firestore, 'articles', articleId);
      await deleteDoc(articleRef);
      
      setArticles(articles.filter(a => a.id !== articleId));
      alert('✅ Article deleted successfully!');
      
    } catch (error) {
      console.error('Error deleting article:', error);
      alert('❌ Error deleting article: ' + error.message);
    }
  };

  const resetArticleForm = () => {
    setArticleFormData({
      title: '',
      excerpt: '',
      content: '',
      category: 'Hair Wigs',
      image: null,
      imagePreview: null,
      date: '',
      readTime: '3 min read'
    });
    setEditingArticle(null);
  };

  // ===== CHECK ADMIN LOGIN =====
  useEffect(() => {
    const isLoggedIn = localStorage.getItem('adminLoggedIn');
    if (!isLoggedIn) {
      window.location.href = '/admin-login';
    }
  }, []);

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      localStorage.removeItem('adminLoggedIn');
      localStorage.removeItem('adminUsername');
      localStorage.removeItem('adminLoginTime');
      window.location.href = '/admin-login';
    }
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="loader"></div>
        <p>Loading products...</p>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      {error && (
        <div className="error-banner">
          <span>⚠️</span>
          <p>{error}</p>
          <button onClick={() => setError('')}>×</button>
        </div>
      )}

      {/* ===== HEADER ===== */}
      <div className="admin-header">
        <div className="header-left">
          <h1>🛠️ Admin Panel</h1>
          <p>Welcome back, <strong>{adminName}</strong>! Manage your products, articles and inventory</p>
        </div>
        <div className="header-right">
          <Link to="/admin-appointments" className="btn-appointments">
            📋 Appointments
          </Link>
          <Link to="/admin-orders" className="btn-appointments">
            📦 Orders
          </Link>
          <button className="btn-logout" onClick={handleLogout}>
            🚪 Logout
          </button>
        </div>
      </div>

      {/* ===== STATS ===== */}
      <div className="admin-stats">
        <div className="stat-card">
          <span className="stat-number">{products.length}</span>
          <span className="stat-label">Total Products</span>
        </div>
        <div className="stat-card">
          <span className="stat-number">{articles.length}</span>
          <span className="stat-label">Total Articles</span>
        </div>
        <div className="stat-card">
          <span className="stat-number">{products.filter(p => p.category === 'men-wigs').length}</span>
          <span className="stat-label">Men Wigs</span>
        </div>
        <div className="stat-card">
          <span className="stat-number">{products.filter(p => p.category === 'new-arrivals').length}</span>
          <span className="stat-label">New Arrivals</span>
        </div>
      </div>

      {/* ===== PRODUCTS SECTION ===== */}
      <div className="products-list">
        <div className="section-header">
          <h2>📦 Products ({products.length})</h2>
          <button 
            className="btn-add-product"
            onClick={() => {
              resetForm();
              setShowForm(true);
            }}
          >
            + Add Product
          </button>
        </div>

        {showForm && (
          <div className="form-overlay">
            <div className="product-form">
              <button className="close-form" onClick={() => {
                setShowForm(false);
                resetForm();
              }}>×</button>
              <h2>{editingProduct ? '✏️ Edit Product' : '➕ Add New Product'}</h2>
              
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Product Title *</label>
                  <input
                    type="text"
                    name="title"
                    placeholder="Enter product title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Description *</label>
                  <textarea
                    name="description"
                    placeholder="Enter product description"
                    value={formData.description}
                    onChange={handleInputChange}
                    required
                    rows="4"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Price *</label>
                    <input
                      type="text"
                      name="price"
                      placeholder="e.g., Rs. 29,999"
                      value={formData.price}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Category *</label>
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="new-arrivals">New Arrivals</option>
                      <option value="men-wigs">Men Wigs</option>
                      <option value="lady-hair">Lady Hair</option>
                      <option value="hair-patches">Hair Patches</option>
                      <option value="hair-units">Hair Units</option>
                      <option value="accessories">Accessories</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Product Image</label>
                  <div className="image-upload-area">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      id="image-upload"
                    />
                    <label htmlFor="image-upload" className="upload-label">
                      {formData.imagePreview ? '📷 Change Image' : '📷 Choose Image'}
                    </label>
                    <span className="upload-hint">JPEG, PNG, GIF, WEBP (Max 5MB)</span>
                  </div>
                  {imageError && <p className="error-message">{imageError}</p>}
                  {formData.imagePreview && (
                    <div className="image-preview">
                      <img src={formData.imagePreview} alt="Preview" />
                      <button 
                        type="button" 
                        className="remove-image"
                        onClick={() => setFormData(prev => ({ ...prev, imagePreview: null, image: null }))}
                      >
                        ×
                      </button>
                    </div>
                  )}
                </div>

                <button type="submit" className="btn-submit">
                  {editingProduct ? '📝 Update Product' : '➕ Add Product'}
                </button>
              </form>
            </div>
          </div>
        )}

        {products.length === 0 ? (
          <div className="empty-state">
            <p>No products found. Add your first product!</p>
          </div>
        ) : (
          <div className="products-grid-admin">
            {products.map((product) => (
              <div className="product-card-admin" key={product.id}>
                <div className="product-image-admin">
                  <img 
                    src={product.image || '/images/default.jpg'} 
                    alt={product.title}
                    onError={(e) => {
                      e.target.src = '/images/default.jpg';
                    }}
                  />
                  <span className="category-tag">{getCategoryLabel(product.category)}</span>
                  {product.isNew && <span className="new-tag">NEW</span>}
                </div>
                <div className="product-info-admin">
                  <h3>{product.title}</h3>
                  <p className="product-price-admin">{product.price}</p>
                  <p className="product-date">📅 {formatDate(product.createdAt)}</p>
                  <div className="product-actions-admin">
                    <button className="btn-edit" onClick={() => handleEdit(product)}>✏️ Edit</button>
                    <button className="btn-delete" onClick={() => handleDelete(product.id)}>🗑️ Delete</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ===== ARTICLES SECTION ===== */}
      <div className="admin-section">
        <div className="section-header">
          <h2>📝 Articles ({articles.length})</h2>
          <button 
            className="btn-add-article"
            onClick={() => {
              resetArticleForm();
              setShowArticleForm(true);
            }}
          >
            + Add Article
          </button>
        </div>

        {showArticleForm && (
          <div className="form-overlay">
            <div className="product-form">
              <button className="close-form" onClick={() => {
                setShowArticleForm(false);
                resetArticleForm();
              }}>×</button>
              <h2>📝 Add New Article</h2>
              <p className="form-hint">Write articles to help customers find you on Google</p>
              
              <form onSubmit={handleAddArticle}>
                <div className="form-group">
                  <label>Article Title *</label>
                  <input
                    type="text"
                    name="title"
                    placeholder="Enter article title (e.g., Best Hair Wigs in Pakistan)"
                    value={articleFormData.title}
                    onChange={handleArticleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Excerpt (Short Description) *</label>
                  <textarea
                    name="excerpt"
                    placeholder="Brief summary of the article (will appear in search results)"
                    value={articleFormData.excerpt}
                    onChange={handleArticleInputChange}
                    rows="2"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Full Content *</label>
                  <textarea
                    name="content"
                    placeholder="Write your full article content here..."
                    value={articleFormData.content}
                    onChange={handleArticleInputChange}
                    rows="6"
                    required
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Category *</label>
                    <select
                      name="category"
                      value={articleFormData.category}
                      onChange={handleArticleInputChange}
                      required
                    >
                      <option value="Hair Wigs">Hair Wigs</option>
                      <option value="Hair Patch">Hair Patch</option>
                      <option value="Hair Extensions">Hair Extensions</option>
                      <option value="Hair Replacement">Hair Replacement</option>
                      <option value="Hair Care">Hair Care</option>
                      <option value="Hair Transplant">Hair Transplant</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Read Time</label>
                    <input
                      type="text"
                      name="readTime"
                      placeholder="e.g., 5 min read"
                      value={articleFormData.readTime}
                      onChange={handleArticleInputChange}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Article Image</label>
                  <div className="image-upload-area">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleArticleImageChange}
                      id="article-image-upload"
                    />
                    <label htmlFor="article-image-upload" className="upload-label">
                      {articleFormData.imagePreview ? '📷 Change Image' : '📷 Upload Image'}
                    </label>
                    <span className="upload-hint">Supports: JPEG, PNG, GIF, WEBP, SVG</span>
                  </div>
                  {articleFormData.imagePreview && (
                    <div className="image-preview">
                      <img src={articleFormData.imagePreview} alt="Preview" />
                      <button 
                        type="button" 
                        className="remove-image"
                        onClick={() => setArticleFormData(prev => ({ ...prev, imagePreview: null, image: null }))}
                      >
                        ×
                      </button>
                    </div>
                  )}
                </div>

                <button type="submit" className="btn-submit">
                  📤 Publish Article
                </button>
              </form>
            </div>
          </div>
        )}

        {articleLoading ? (
          <div className="loading-spinner">Loading articles...</div>
        ) : articles.length === 0 ? (
          <div className="empty-state">
            <p>No articles yet. Write your first article to boost SEO!</p>
          </div>
        ) : (
          <div className="articles-grid-admin">
            {articles.map((article) => (
              <div className="article-card-admin" key={article.id}>
                <div className="article-image-admin">
                  <img 
                    src={article.image || '/images/default-article.jpg'} 
                    alt={article.title}
                    onError={(e) => {
                      e.target.src = '/images/default-article.jpg';
                    }}
                  />
                  <span className="article-category-tag">{article.category}</span>
                </div>
                <div className="article-info-admin">
                  <h3>{article.title}</h3>
                  <p className="article-excerpt-admin">{article.excerpt}</p>
                  <div className="article-meta-admin">
                    <span>📅 {article.date}</span>
                    <span>⏱️ {article.readTime}</span>
                  </div>
                  <div className="article-actions-admin">
                    <button className="btn-edit">✏️ Edit</button>
                    <button className="btn-delete" onClick={() => handleDeleteArticle(article.id)}>🗑️ Delete</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;